// Toss Payments V2 SDK(@tosspayments/tosspayments-sdk) 빌링(자동결제) 래퍼.
// 카드·계좌 등록 인증창 호출까지만 담당 — 성공 시 successUrl 쿼리로 authKey·customerKey 회신되며,
// 빌링키 발급(POST /billings {authKey, customerKey})은 후속(BE) 단계라 여기 범위 밖.
// client key 는 환경별 `env.tossClientKey`(공개값) — 정책: docs/payment-method-decisions.md §2.
import { loadTossPayments } from '@tosspayments/tosspayments-sdk';
import { env } from '@/env';

/** 빌링 등록 결제수단 — 카드 자동결제 / 계좌 자동이체. SDK `method`('CARD'/'TRANSFER')로 매핑. */
export type BillingRegisterMethod = 'card' | 'transfer';

export interface BillingAuthParams {
  /** 등록할 결제수단(카드 자동결제 / 계좌 자동이체). */
  method: BillingRegisterMethod;
  /**
   * 빌링 customerKey — 사용자 고유 식별자. 인증 성공 시 successUrl 쿼리로 그대로 회신되어
   * BE 빌링키 발급에 사용된다. 시크릿키(`_sk_`) 사용 금지(InsecureKeyUsageError).
   */
  customerKey: string;
  /** 인증 성공 리다이렉트(authKey·customerKey 쿼리 수신). 반드시 origin 포함 절대 URL. */
  successUrl: string;
  /** 인증 실패 리다이렉트(에러코드·메시지 쿼리 수신). 반드시 origin 포함 절대 URL. */
  failUrl: string;
  /** 구매자명(선택) — 상점관리자·영수증 이메일 노출용. */
  customerName?: string;
  /** 구매자 이메일(선택) — 결제내역 전송용. */
  customerEmail?: string;
}

/** 토스 SDK 가 사용자가 인증창을 닫았을 때 던지는 에러 코드 — 정상 취소라 토스트하지 않는다. */
const TOSS_USER_CANCEL = 'USER_CANCEL';

/** 인증창을 사용자가 닫아 reject 된 경우(정상 취소) 판별 — 호출처가 토스트 억제에 사용. */
export function isTossUserCancel(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    (error as { code?: unknown }).code === TOSS_USER_CANCEL
  );
}

/** 인증 **성공** 리다이렉트 시 Toss 가 successUrl 에 부착하는 쿼리. */
export interface TossBillingSuccessQuery {
  /** payment({customerKey}) 에 넘긴 값 그대로 회신. */
  customerKey: string;
  /** 빌링키 발급(POST /billings)에 쓰는 1회용 인증키(최대 300자). */
  authKey: string;
}

/** 인증 **실패** 리다이렉트 시 Toss 가 failUrl 에 부착하는 쿼리. */
export interface TossBillingFailQuery {
  /** 에러 코드(예: `USER_CANCEL`). */
  code: string;
  /** 사용자 노출용 에러 메시지. */
  message: string;
}

/**
 * 토스 빌링 **결제수단 등록** 인증창 호출(`method` 로 카드/계좌 분기). 호출 시 등록창으로 전환되고,
 * 완료되면 success/failUrl 로 리다이렉트된다. 사용자가 창을 닫으면 reject(`UserCancelError`)하므로
 * 호출처가 catch(`isTossUserCancel`)한다.
 */
export async function requestBillingAuth({
  method,
  customerKey,
  successUrl,
  failUrl,
  customerName,
  customerEmail,
}: BillingAuthParams): Promise<void> {
  const tossPayments = await loadTossPayments(env.tossClientKey);
  const payment = tossPayments.payment({ customerKey });
  // SDK 의 requestBillingAuth 는 method 판별자 유니온이라 변수 method 를 그대로 못 넘김 → 분기.
  if (method === 'card') {
    await payment.requestBillingAuth({
      method: 'CARD',
      successUrl,
      failUrl,
      customerName,
      customerEmail,
    });
  } else {
    await payment.requestBillingAuth({
      method: 'TRANSFER',
      successUrl,
      failUrl,
      customerName,
      customerEmail,
    });
  }
}
